import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './MobilePanel.css';
import { getString } from '../../utils/languageUtils';
import Contact from '../contact/Contact';
import Search from '../search/Search';
import rawData from '../../utils/tabsData.json';
import { TabsData } from '../../types/navigation';
import { useNavigationTranslation } from '../../hooks/useNavigationTranslation';
import { parseUrlToNavigationContext } from '../../utils/navigationUtils';

const data = rawData as TabsData;

interface MobilePanelProps {
  onTabChange: (tabId: string) => void;
  activeTab: string | null;
  setActiveTab: React.Dispatch<React.SetStateAction<string | null>>;
  clickedTab: string | null;
  setClickedTab: React.Dispatch<React.SetStateAction<string | null>>;
  clickedSubTab: string | null;
  setClickedSubTab: (subTabId: string | null) => void;
  setShowBackground: (show: boolean) => void;
  activeProduct: string | null;
  setActiveProduct: React.Dispatch<React.SetStateAction<string | null>>;
  onLogoClick: () => void;
  resetActiveProduct: () => void;
  isMobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
  isMobile: boolean;
  bgImgRef: React.RefObject<HTMLDivElement>;
  headerRef: React.RefObject<HTMLDivElement>;
}

const MobilePanel: React.FC<MobilePanelProps> = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const [selectedSubTab, setSelectedSubTab] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { getTabLabel, getSubTabLabel, getProductLabel } = useNavigationTranslation();

  useEffect(() => {
    if (props.isMobileMenuOpen) {
      setIsVisible(true);
      setTimeout(() => setIsOpen(true), 20);
      
      // Set menu expansion based on current URL
      const navigationContext = parseUrlToNavigationContext(location.pathname, data);
      if (navigationContext.activeTab) {
        setSelectedTab(navigationContext.activeTab);
        if (navigationContext.activeSubTab) {
          setSelectedSubTab(navigationContext.activeSubTab);
        }
      }
    } else {
      setIsOpen(false);
      setTimeout(() => {
        setIsVisible(false);
        // Reset menu state when closing
        setSelectedTab(null);
        setSelectedSubTab(null);
      }, 200);
    }
  }, [props.isMobileMenuOpen, location.pathname]);

  const handleTabClick = (tabId: string) => {
    if (selectedTab === tabId) {
      setSelectedTab(null);
      setSelectedSubTab(null);
    } else {
      setSelectedTab(tabId);
      // If tab has only one subtab, automatically select it
      const tab = data.tabs.find(t => t.id === tabId);
      const visibleSubtabs = tab?.subtabs?.filter(subtab => !subtab.isHidden) || [];
      if (visibleSubtabs.length === 1) {
        setSelectedSubTab(visibleSubtabs[0].id);
      } else {
        setSelectedSubTab(null);
      }
    }
    props.setActiveTab(tabId);
    props.setClickedTab(tabId);
  };

  const handleSubTabClick = (subtabId: string) => {
    setSelectedSubTab(selectedSubTab === subtabId ? null : subtabId);
  };

  const handleProductClick = (tabId: string, subtabId: string, productId: string) => {
    props.toggleMobileMenu();
    navigate(`/${subtabId}/${productId}`);
  };

  if (!isVisible) return null;

  return (
    <div className={`mobile-panel ${isOpen ? 'open' : ''}`}>
      <div className="mobile-panel-content">
        <div className="mobile-nav-section">
          {data.tabs
            .filter(tab => !tab.isHidden)
            .map((tab) => {
              const visibleSubtabs = tab.subtabs?.filter(subtab => !subtab.isHidden) || [];
              const hasSingleSubtab = visibleSubtabs.length === 1;
              const singleSubtab = hasSingleSubtab ? visibleSubtabs[0] : null;

              return (
                <div key={tab.id} className="mobile-nav-item">
                  <div
                    className={`mobile-nav-tab ${selectedTab === tab.id ? 'active' : ''}`}
                    onClick={() => handleTabClick(tab.id)}
                  >
                    <span>{getTabLabel(tab.id)}</span>
                    <span className="mobile-nav-arrow">
                      {selectedTab === tab.id ? '−' : '+'}
                    </span>
                  </div>
                  {selectedTab === tab.id && tab.subtabs && (
                    <div className="mobile-nav-subtabs">
                      {hasSingleSubtab && singleSubtab && singleSubtab.products ? (
                        // If there's only one subtab with products, show products directly
                        <div className="mobile-nav-products">
                          {singleSubtab.products
                            .filter(product => !product.isHidden)
                            .map((product) => (
                              <div
                                key={product.id}
                                className="mobile-nav-product"
                                onClick={() => handleProductClick(tab.id, singleSubtab.id, product.id)}
                              >
                                {getProductLabel(product.id)}
                              </div>
                            ))}
                        </div>
                      ) : (
                        // Otherwise show subtabs as before
                        visibleSubtabs.map((subtab) => (
                          <div key={subtab.id} className="mobile-nav-subtab">
                            <div
                              className={`mobile-subtab-header ${selectedSubTab === subtab.id ? 'active' : ''}`}
                              onClick={() => handleSubTabClick(subtab.id)}
                            >
                              <span>{getSubTabLabel(subtab.id)}</span>
                              {subtab.products && subtab.products.length > 0 && (
                                <span className="mobile-nav-arrow">
                                  {selectedSubTab === subtab.id ? '−' : '+'}
                                </span>
                              )}
                            </div>
                            {selectedSubTab === subtab.id && subtab.products && (
                              <div className="mobile-nav-products">
                                {subtab.products
                                  .filter(product => !product.isHidden)
                                  .map((product) => (
                                    <div
                                      key={product.id}
                                      className="mobile-nav-product"
                                      onClick={() => handleProductClick(tab.id, subtab.id, product.id)}
                                    >
                                      {getProductLabel(product.id)}
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>

    </div>
  );
};

export default MobilePanel;